import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import FAQ from "@/components/FAQ";
import Breadcrumbs from "@/components/Breadcrumbs";
import OptimizedImage from "@/components/OptimizedImage";
import { Phone, CheckCircle2, Sparkles, Shield, Clock, Droplets, Home, Ruler, Wrench } from "lucide-react";
import luxuryCurblessShower from "@/assets/luxury-curbless-shower-niagara.jpg";
import luxuryEnsuite from "@/assets/luxury-ensuite-freestanding-tub.jpg";
import tubToShower from "@/assets/tub-to-shower-conversion-niagara.jpg";
import smallBathroom from "@/assets/small-bathroom-makeover.jpg";
import tileWork from "@/assets/bathroom-tile-installation-work.jpg";
import powderRoom from "@/assets/powder-room-refresh.jpg";

const BathroomRenovations = () => {
  const faqs = [
    {
      question: "What is a realistic budget for a bathroom renovation in Niagara, Hamilton, or Toronto GTA?",
      answer:
        "In Niagara, Hamilton, and the GTA, small refreshes often start around $8k–$15k CAD; full 3-piece baths typically fall $18k–$35k CAD; luxury primary suites with custom tile, glass, and heated floors can range $40k–$90k+ CAD. Actual cost depends on size, materials, and scope.",
    },
    {
      question: "How much should you spend on a bathroom remodel?",
      answer:
        "A common guideline is 5–10% of your home's value for a main bath or ensuite. Prioritize waterproofing, ventilation, and durable finishes to protect your investment.",
    },
    {
      question: "Can you renovate a bathroom for $10,000 in the Toronto GTA?",
      answer:
        "Yes—if scope stays tight: keep the layout, select mid-range fixtures, use cost-smart tile or panels, and avoid plumbing/electrical relocations. Cosmetic upgrades plus a prefabricated shower can fit that envelope.",
    },
    {
      question: "What is the most expensive part of a bathroom remodel in Niagara and Hamilton?",
      answer:
        "Custom tile/stone work often leads costs, followed by plumbing/electrical relocations and custom glass. Curbless showers and layout changes increase labour and waterproofing needs.",
    },
    {
      question: "How long does a full bathroom renovation take?",
      answer:
        "Most projects run 2–4 weeks once work begins; complex luxury builds may take 4–6 weeks. Lead times for specialty items can affect start dates.",
    },
    {
      question: "Do I need to move out during the bathroom remodel?",
      answer:
        "Usually not. We isolate dust, protect floors, and keep noise hours reasonable. If you have only one bathroom, we'll schedule work to minimize downtime.",
    },
    {
      question: "Do you handle permits and inspections in Niagara, Hamilton, and Toronto GTA?",
      answer:
        "Yes. We coordinate permits and inspections with your local municipality in Niagara, Hamilton, Toronto, and throughout the GTA, ensuring all work meets Ontario Building Code requirements.",
    },
    {
      question: "Do you provide accessibility upgrades for bathrooms in the GTA?",
      answer:
        "Absolutely: curbless showers, grab bars anchored into blocking, comfort-height toilets, wider doors, and slip-resistant tile for homes throughout Niagara Region, Hamilton, and Toronto GTA.",
    },
  ];

  const galleryImages = [
    {
      src: luxuryCurblessShower,
      alt: "Curbless shower with linear drain and large-format porcelain tile — Niagara renovation",
    },
    { src: luxuryEnsuite, alt: "Luxury ensuite with freestanding tub and custom vanity — Welland home" },
    { src: tubToShower, alt: "Tub-to-shower conversion with glass enclosure — St. Catharines" },
    { src: smallBathroom, alt: "Small bathroom makeover with space-saving layout — Niagara Falls" },
    { src: tileWork, alt: "Custom bathroom tile installation with waterproofing — Niagara-on-the-Lake" },
    { src: powderRoom, alt: "Powder room refresh with modern fixtures — Thorold" },
  ];

  const processes = [
    {
      icon: <Ruler className="h-8 w-8" />,
      title: "Design & Estimate",
      description: "Measure, 3D concepts, and a line-item quote.",
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: "Materials & Scheduling",
      description: "We lock fixtures/finishes and set a realistic start date.",
    },
    {
      icon: <Wrench className="h-8 w-8" />,
      title: "Demolition & Waterproofing",
      description: "Dust control, careful removal, plumbing/electrical rough-ins, waterproofing.",
    },
    {
      icon: <Sparkles className="h-8 w-8" />,
      title: "Installations & Finishes",
      description: "Tile, glass, cabinetry, paint, trim, mirrors, and accessories.",
    },
    {
      icon: <CheckCircle2 className="h-8 w-8" />,
      title: "Final Walkthrough & Warranty",
      description: "Punch-list perfection and documented care tips.",
    },
  ];

  const serviceAreas = [
    "Niagara Region (all cities)",
    "Hamilton & Burlington",
    "Toronto & GTA",
    "Welland (home base)",
    "St. Catharines",
    "Niagara Falls",
    "Niagara-on-the-Lake",
    "Thorold",
    "Fort Erie",
    "Port Colborne",
    "Pelham",
    "Grimsby",
    "Lincoln",
    "West Lincoln",
    "Mississauga",
    "Brampton",
    "Vaughan",
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "HomeAndConstructionBusiness",
        "@id": "https://www.extreme-renovations.ca/#business",
        name: "Extreme Renovations",
        image: luxuryCurblessShower,
        url: window.location.origin,
        telephone: "905-687-2208",
        priceRange: "$$$",
        description:
          "Experience luxury bathroom transformations with Extreme Renovations. We specialize in bathroom renovations across Niagara Region, Hamilton, and Toronto GTA, offering custom bathroom design, expert bathroom remodel services, and modern upgrades tailored to your style.",
        address: {
          "@type": "PostalAddress",
          streetAddress: "10 Federal Road",
          addressLocality: "Welland",
          addressRegion: "ON",
          postalCode: "L3B 3P2",
          addressCountry: "CA",
        },
        openingHours: "Mo,Tu,We,Th,Fr 09:00-17:00",
        areaServed: [
          {
            "@type": "City",
            name: "Niagara Falls"
          },
          {
            "@type": "City",
            name: "St. Catharines"
          },
          {
            "@type": "City",
            name: "Hamilton"
          },
          {
            "@type": "City",
            name: "Toronto"
          },
          {
            "@type": "City",
            name: "Mississauga"
          },
          {
            "@type": "City",
            name: "Welland"
          }
        ],
      },
      {
        "@type": "Service",
        "@id": `${window.location.origin}/#bathroom-service`,
        serviceType: "Bathroom Renovation",
        name: "Bathroom Renovations Niagara, Hamilton, Toronto GTA | Extreme Renovations",
        provider: { "@id": "https://www.extreme-renovations.ca/#business" },
        areaServed: [
          { "@type": "Place", name: "Niagara Region" },
          { "@type": "Place", name: "Hamilton" },
          { "@type": "Place", name: "Toronto GTA" }
        ],
        offers: {
          "@type": "Offer",
          url: window.location.href,
          category: "Remodeling",
        },
        keywords: [
          "Extreme Renovations",
          "bathroom renovation Niagara",
          "bathroom remodel Hamilton",
          "bathroom design Toronto GTA",
          "full bathroom renovations",
          "luxury bathrooms",
          "remodel bathrooms",
          "bathroom contractors Niagara",
          "Hamilton bathroom renovations",
          "Toronto bathroom remodeling",
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta
        title="Bathroom Renovations Niagara, Hamilton, Toronto GTA | Luxury & Full Remodels"
        description="Expert bathroom renovations in Niagara Region, Hamilton, and Toronto GTA. Full bathroom remodels, luxury designs, tub-to-shower conversions, waterproofing. Licensed contractors. Free consultation: 905-687-2208."
        keywords="bathroom renovations Niagara, bathroom remodel Hamilton, bathroom design Toronto GTA, luxury bathrooms Niagara Region, tub-to-shower conversion, bathroom contractors Hamilton, curbless shower, heated floors, bathroom remodeling Toronto, Welland bathroom renovations, St. Catharines, Niagara Falls, Mississauga"
        ogImage={luxuryCurblessShower}
        schemaType="LocalBusiness"
        schemaData={schemaData}
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="secondary" className="mb-4">
              Full Bathroom Renovations
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Bathroom Renovations in Niagara, Hamilton & Toronto GTA — Luxury, Function, Wow
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Extreme Renovations makes bathroom remodels easy across Niagara Region, Hamilton, and the Greater Toronto Area (yes, really)
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="cta" className="gap-2">
                <Phone className="h-5 w-5" />
                Call 905-687-2208
              </Button>
              <Button size="lg" variant="outline">
                Request Free Consult
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Paragraph */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-muted-foreground leading-relaxed">
              Dreaming of a bathroom that feels like a boutique hotel but works like a Swiss watch? Extreme Renovations
              designs and builds full bathroom renovations across Niagara Region, Hamilton, and the Greater Toronto Area—combining smart layouts, durable
              waterproofing, and luxury finishes that actually survive real life. From tub-to-shower conversions to
              curbless spa suites with heated floors, our team handles design, permits, and build with a clean jobsite
              and predictable schedule. Serving St. Catharines, Niagara Falls, Hamilton, Burlington, Toronto, Mississauga, and beyond. Planning a complete home renovation? Explore our{" "}
              <Link to="/services/kitchen-renovations" className="text-primary hover:underline font-medium">
                kitchen renovation services
              </Link>{" "}
              for a full transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Why Homeowners in Niagara, Hamilton & Toronto GTA Choose Us</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="border-2 hover:border-primary/50 transition-all">
              <CardContent className="p-6">
                <Sparkles className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Luxury Design That Fits Real Life</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>
                      Design-first approach with storage, lighting, and ventilation planned before tile shopping
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>Luxury finishes installed by specialists—stone, glass, and premium fixtures</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all">
              <CardContent className="p-6">
                <Clock className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Dust-Smart, Schedule-Tight, Budget-Clear Process</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>Daily site cleanup, floor protection, and dust control</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>Predictable schedules with realistic timelines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>Clear line-item quotes before work begins</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all">
              <CardContent className="p-6">
                <Shield className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Fully Insured, WSIB-Covered, and Permit-Friendly</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>Based in Welland, serving Niagara Region, Hamilton, and Toronto GTA</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>We coordinate permits and inspections with local municipalities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>Ontario Building Code compliant work guaranteed</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Recent Bathroom Projects in Niagara, Hamilton & Toronto GTA</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              From powder rooms to luxury ensuite spa retreats across Niagara Region, Hamilton, and the GTA
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {galleryImages.map((image, index) => (
              <Card key={index} className="overflow-hidden group cursor-pointer hover:shadow-2xl transition-all">
                <div className="relative h-64 overflow-hidden">
                  <OptimizedImage
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading={index < 3 ? "eager" : "lazy"}
                    priority={index < 3}
                  />
                </div>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">{image.alt}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Full Bathroom Renovations */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Full Bathroom Renovations (Gut-to-Gorgeous)</h2>
            <p className="text-lg text-muted-foreground mb-6">
              From gut-to-studs to tasteful refreshes, we re-think layouts, move plumbing (when it's worth it), improve
              lighting, and upgrade ventilation. Our certified{" "}
              <Link to="/services/plumber" className="text-primary hover:underline font-medium">
                plumbers
              </Link>{" "}
              and{" "}
              <Link to="/services/electrician" className="text-primary hover:underline font-medium">
                electricians
              </Link>{" "}
              ensure every installation meets code. We also provide expert{" "}
              <Link to="/services/flooring" className="text-primary hover:underline font-medium">
                tile and flooring installation
              </Link>
              .
            </p>
            <ul className="grid md:grid-cols-2 gap-4">
              {[
                "Custom tiled showers with inset niches",
                "Quartz or natural stone vanities",
                "In-floor heating systems",
                "Quiet ventilation fans that actually clear steam",
                "Layout reworks for better flow",
                "Complete waterproofing with Schluter-style systems",
                "Accessibility upgrades when needed",
                "Upgraded lighting and electrical",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Luxury Bathrooms */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Luxury Bathrooms (Spa Vibes, Daily)</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Want hotel-level calm? Go curbless, add linear drains, choose a freestanding soaking tub, integrate
              dimmable LED lighting, and finish with slab-look tile or natural stone. Smart shower controls remember
              temps; heated floors make winter mornings… less winter. It's daily luxury—no valet needed.
            </p>
            <ul className="grid md:grid-cols-2 gap-4">
              {[
                "Curbless showers with linear drains",
                "Freestanding soaking tubs",
                "Slab-look tile or natural stone finishes",
                "Smart shower controls with temperature memory",
                "Heated floors throughout",
                "Custom niche lighting",
                "Frameless custom glass enclosures",
                "High-end fixtures and hardware",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Sparkles className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Conversions & Small Baths */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Tub-to-Shower Conversions & Small Bath Makeovers</h2>
            <p className="text-lg text-muted-foreground mb-6">
              No space? No problem. We swap tubs for low-maintenance showers, add storage (hello, tall linen cabinets),
              choose large-format tile to visually expand rooms, and use glass to keep sightlines open.
            </p>
            <ul className="grid md:grid-cols-2 gap-4">
              {[
                "Space-saving layout optimization",
                "Tall linen cabinets and smart storage",
                "Large-format tile for visual expansion",
                "Frameless glass to maintain sightlines",
                "Stylish designs on sensible budgets",
                "Low-maintenance shower systems",
                "Modern fixtures and finishes",
                "Efficient use of every square inch",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Home className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 5-Step Process */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Our 5-Step Process</h2>
            <p className="text-xl text-muted-foreground">From concept to completion, streamlined and transparent</p>
          </div>

          <div className="grid md:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {processes.map((process, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4 text-primary">{process.icon}</div>
                  <div className="text-sm font-bold text-primary mb-2">Step {index + 1}</div>
                  <h3 className="font-bold mb-3">{process.title}</h3>
                  <p className="text-sm text-muted-foreground">{process.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 text-center">Transparent Pricing</h2>
            <p className="text-lg text-muted-foreground mb-8 text-center">Typical ranges in Niagara, Hamilton & Toronto GTA (CAD)</p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="border-2">
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold mb-2">Powder Room Refresh</h3>
                  <div className="text-3xl font-bold text-primary mb-4">$8k–$15k+</div>
                  <p className="text-sm text-muted-foreground">Cosmetic updates, new fixtures, fresh finishes</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-primary">
                <CardContent className="p-6 text-center">
                  <Badge variant="default" className="mb-4">
                    Most Popular
                  </Badge>
                  <h3 className="text-xl font-bold mb-2">3-Piece Full Bath</h3>
                  <div className="text-3xl font-bold text-primary mb-4">$18k–$35k+</div>
                  <p className="text-sm text-muted-foreground">Complete renovation, quality finishes, smart layouts</p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold mb-2">Luxury Primary Suite</h3>
                  <div className="text-3xl font-bold text-primary mb-4">$40k–$90k+</div>
                  <p className="text-sm text-muted-foreground">Spa-level finishes, custom everything, heated floors</p>
                </CardContent>
              </Card>
            </div>

            <p className="text-sm text-muted-foreground text-center">
              Ranges vary with size, plumbing moves, tile/stone choices, and custom features. We'll price yours clearly
              before we start.
            </p>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Serving Niagara Region, Hamilton & Toronto GTA</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Based in Welland, we provide expert bathroom renovation services throughout Niagara Region, Hamilton, Burlington, and the Greater Toronto Area including{" "}
              <Link to="/toronto" className="text-primary hover:underline font-medium">
                Toronto
              </Link>
              ,{" "}
              <Link to="/mississauga" className="text-primary hover:underline font-medium">
                Mississauga
              </Link>
              ,{" "}
              <Link to="/hamilton" className="text-primary hover:underline font-medium">
                Hamilton
              </Link>
              , and all cities across the GTA.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {serviceAreas.map((area, index) => (
                <Badge key={index} variant="secondary" className="text-sm px-4 py-2">
                  {area}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <FAQ
        title="Frequently Asked Questions"
        description="Common questions about bathroom renovations in Niagara Region, Hamilton, and Toronto GTA"
        faqs={faqs}
      />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Plan Your Bathroom?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Ready for a bathroom that actually sparks joy? Call us or book your free consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="cta" className="gap-2">
                <Phone className="h-5 w-5" />
                Call 905-687-2208
              </Button>
              <Button size="lg" variant="outline">
                Request Free Consult
              </Button>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">Available Weekdays 9:00 AM – 5:00 PM</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BathroomRenovations;
